import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TableList = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [count, setCount] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [leagues, setLeagues] = useState([]);
  const [leagueSearch, setLeagueSearch] = useState("");
  const [filteredLeagues,setfilteredleague] = useState([]);
  const [selectedleague,setSelectedLeague] = useState("");
  const [filteredtable,setfilteredtable] = useState([])

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const fetchTables = async () => {
    setLoading(true);
     try {
          let url = ENDPOINT.TABLE_LIST;
          let allResults = [];
    
          while (url) {
            const res = await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` },
            });
    
            allResults = [...allResults, ...res.data.results];
            url = res.data.next;
          }
  
          setTables(allResults);
           setLoading(false);
        } catch (error) {
          console.error("Error loading table list:", error);
          toast.error("Failed to load table");
        }
  };

  const fetchAllLeagues = async () => {
      try {
        let url = ENDPOINT.LEAGUECATEGORY_LIST;
        let allResults = [];
  
        while (url) {
          const res = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
          });
  
          allResults = [...allResults, ...res.data.results];
          url = res.data.next; // load next pages
        }
  
        setLeagues(allResults);
        setfilteredleague(allResults)
        setSelectedLeague(allResults[0].id)
      } catch (error) {
        console.error("Error loading league list:", error);
        toast.error("Failed to load leagues");
      }
    };

     useEffect(()=>{
      let filteredLeague = leagues.filter((l) =>
        l.name.toLowerCase().includes(leagueSearch.toLowerCase())
      );
      setfilteredleague(filteredLeague)
      },[leagueSearch])

    useEffect(() => {
  if (selectedleague && tables.length > 0) {
    const filtered = tables.filter(
      (t) => t.league.id === selectedleague
    );
    setfilteredtable(filtered);
    setCount(filtered.length);
  }
}, [selectedleague, tables]);


  useEffect(() => {
    if (!token) navigate("/login");
    else {
      fetchAllLeagues();
      fetchTables();
    };
  }, []);

  // ============================
  // SEARCH
  // ============================
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTables();
  };

  // ============================
  // DELETE
  // ============================
  const handleDelete = async () => {
    try {
      await axios.delete(ENDPOINT.TABLE_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Table deleted");
      fetchTables();
    } catch {
      toast.error("Delete failed");
    } finally {
      setOpenDialog(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <ToastContainer />

      {/* SEARCH */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search table..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" sx={{ ml: 1 }}>
            Search
          </Button>
        </Box>

        <FormControl style={{width:300}}>
                      <InputLabel id="league-label">League</InputLabel>
        
                      <Select
                        labelId="league-label"
                        label="League"
                        name="league_id"
                        value={selectedleague}
                        onChange={(e)=>setSelectedLeague(e.target.value)}
                        required
                        MenuProps={{ PaperProps: { style: { maxHeight: 350 } } }}
                      >
                        {/* Search Box */}
                        <MenuItem disableRipple>
                          <TextField
                            fullWidth
                            placeholder="Search league..."
                            size="small"
                            value={leagueSearch}
                            onChange={(e) => setLeagueSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()} 
                          />
                        </MenuItem>
        
                        <MenuItem disabled>
                          <Typography variant="caption" color="text.secondary">
                            {filteredLeagues.length} results
                          </Typography>
                        </MenuItem>
        
                        {filteredLeagues.map((l) => (
                          <MenuItem key={l.id} value={l.id}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                component="img"
                                src={`${IMAGE_BASE_URL}${l.image}`}
                                alt={l.name}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  mr: 1.5,
                                }}
                              />
                              {l.name}
                            </Box>
                          </MenuItem>
                        ))}
        
                        {filteredLeagues.length === 0 && (
                          <MenuItem disabled>No results found</MenuItem>
                        )}
                      </Select>
                    </FormControl>

        <Button variant="contained" onClick={() => navigate("/table/create")}>
          + Create
        </Button>
      </Stack>

      {/* TABLE */}
      {loading ? (
        <Box textAlign="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pos</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>PL</TableCell>
                  <TableCell>W</TableCell>
                  <TableCell>D</TableCell>
                  <TableCell>L</TableCell>
                  <TableCell>+/-</TableCell>
                  <TableCell>GD</TableCell>
                  <TableCell>PTS</TableCell>
                  <TableCell>Form</TableCell>
                  <TableCell>League</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredtable.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.position}</TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={`${IMAGE_BASE_URL}${item.team.image}`} />
                        {item.team.name}
                      </Stack>
                    </TableCell>

                    <TableCell>{item.pl}</TableCell>
                    <TableCell>{item.w}</TableCell>
                    <TableCell>{item.d}</TableCell>
                    <TableCell>{item.l}</TableCell>
                    <TableCell>{item.plusandminus}</TableCell>
                    <TableCell>{item.gd}</TableCell>
                    <TableCell>{item.pts}</TableCell>

                    {/* FORM FIXED */}
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {item.form?.map((f, i) => (
                          <Chip
                      label={f.label}
                      color={f.color}
                      size="small"
                       sx={{
                        backgroundColor: f.color,
                        color: "#ffffff",
                        fontWeight: 300
                      }}
                    />
                        ))}
                      </Box>
                    </TableCell>

                    <TableCell>{item.league.name}</TableCell>

                    <TableCell align="center">
                      <IconButton onClick={() => navigate(`/table/update/${item.id}`)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setDeleteId(item.id);
                          setOpenDialog(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          <Divider />
          <Stack direction="row" justifyContent="space-between" p={2}>
            <Typography>Total: {count}</Typography>
          </Stack>
        </Paper>
      )}

      {/* DELETE DIALOG */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this table?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TableList;
